import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { authFetch } from '../../auth';

const SITE = '/api/cms/site-info';
const OFFICES = '/api/cms/offices';
const SECTIONS = '/api/cms/sections';
const SOL = '/api/cms/solutions';
const APP = '/api/cms/app-chapters';
const PTN = '/api/cms/partners';

/**
 * 푸터 — 회사 기본정보(TB_SITE_INFO) + 푸터 머리글/소개 문구 편집.
 * 솔루션·적용분야·파트너사 목록은 실데이터(응용분야/적용분야/파트너 순서)를 그대로 표시(자동, 편집 없음).
 * 헤더 메뉴가 공유하는 nav·categories 섹션은 여기서 더 이상 수정하지 않는다(저장에서 제외 → 기존값 보존).
 */
export default function Footer({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [site, setSite] = useState(undefined);
  const [offices, setOffices] = useState([]);
  const [secs, setSecs] = useState({});
  const [sols, setSols] = useState([]);
  const [apps, setApps] = useState([]);
  const [ptns, setPtns] = useState([]);
  const ref = useRef(null);
  const secRef = useRef({});
  const dirtyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      authFetch(`${SITE}?lang=ko`).then((r) => r.json()),
      authFetch(`${OFFICES}?lang=ko`).then((r) => r.json()),
      authFetch(`${SECTIONS}?page=global&lang=ko`).then((r) => r.json()),
      authFetch(`${SOL}?lang=ko`).then((r) => r.json()),
      authFetch(`${APP}?lang=ko`).then((r) => r.json()),
      authFetch(`${PTN}?lang=ko`).then((r) => r.json()),
    ]).then(([s, o, sec, sl, ap, pt]) => {
      if (!alive) return;
      ref.current = s; setSite(s); setOffices(o);
      const m = {};
      (sec || []).forEach((x) => { m[x.key] = x.data || {}; });
      secRef.current = m; setSecs(m);
      setSols(sl || []); setApps(ap || []); setPtns(pt || []);
    }).catch(() => { if (alive) { setSite(null); } });
    return () => { alive = false; };
  }, [refreshKey]);

  const update = (f, v) => { dirtyRef.current = true; ref.current = { ...ref.current, [f]: v }; setSite(ref.current); };
  const updSec = (key, field, v) => {
    dirtyRef.current = true;
    secRef.current = { ...secRef.current, [key]: { ...(secRef.current[key] || {}), [field]: v } };
    setSecs(secRef.current);
  };

  const save = useCallback(async () => {
    const s = ref.current;
    const r1 = await authFetch(SITE, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ company: s.company, ceo: s.ceo, bizNo: s.bizNo, copyright: s.copyright, footerDesc: s.footerDesc }),
    });
    if (!r1.ok) throw new Error(`회사정보 저장 실패 (${r1.status})`);

    // 푸터 머리글(footer)만 저장. nav·categories는 헤더가 공유하므로 건드리지 않음(기존 번역/값 보존).
    const data = secRef.current.footer;
    if (data) {
      const res = await authFetch(SECTIONS, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: 'global', key: 'footer', type: 'ui', sort: 3, dataKo: data }),
      });
      if (!res.ok) throw new Error(`푸터 문구 저장 실패 (${res.status})`);
    }
    dirtyRef.current = false;
  }, []);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current, []);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (site === undefined) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }
  const s = site || {};
  const foot = secs.footer || {};

  return (
    <footer className="site-footer" style={{ marginTop: 0 }}>
      <div className="footer-grid">
        <div className="footer-col">
          <h4>AceTec</h4>
          <Editable as="p" style={{ marginBottom: 'var(--spacing-lg)' }} value={s.footerDesc || ''} onChange={(t) => update('footerDesc', t)} />
          {offices.map((o) => (
            <p className="footer-contact" key={o.intNum}>
              <strong>{o.name}</strong><br />
              {o.addr}<br />
              {o.email ? <>{o.email}<br /></> : null}
              {o.tel}<br />
              {o.fax}
            </p>
          ))}
        </div>
        <div className="footer-col">
          <Editable as="h4" value={foot.colSolutions || '솔루션'} onChange={(t) => updSec('footer', 'colSolutions', t)} />
          {sols.map((x) => <a key={x.slug}>{x.title}</a>)}
          <p className="footer-auto-note">응용 분야 순서대로 자동 표시</p>
        </div>
        <div className="footer-col">
          <h4>적용 분야</h4>
          {apps.map((x) => <a key={x.intNum != null ? x.intNum : x.title}>{x.title}</a>)}
          <p className="footer-auto-note">적용 분야 순서대로 자동 표시</p>
        </div>
        <div className="footer-col">
          <Editable as="h4" value={foot.colPartners || '파트너사'} onChange={(t) => updSec('footer', 'colPartners', t)} />
          {ptns.filter((x) => !x.type || x.type === 0).slice(0, 9).map((x) => <a key={x.intNum != null ? x.intNum : x.name}>{x.name}</a>)}
          <p className="footer-auto-note">파트너사(type=0) 최대 9개 자동 표시</p>
        </div>
      </div>
      <div className="footer-bottom">
        <Editable value={s.company || ''} onChange={(t) => update('company', t)} />
        {' · '}
        <Editable value={s.ceo || ''} onChange={(t) => update('ceo', t)} />
        {' · '}
        {(foot.bizLabel || '사업자등록번호') + ': '}
        <Editable value={s.bizNo || ''} onChange={(t) => update('bizNo', t)} />
        <br />
        <Editable value={s.copyright || ''} onChange={(t) => update('copyright', t)} />
      </div>
    </footer>
  );
}
